import { type MouseEvent as ReactMouseEvent, type ReactNode, useCallback } from 'react';
import styles from './ActiveZone.module.css';

interface Props {
  children: ReactNode;
  className?: string;
  testId?: string;
}

const INTERACTIVE_SELECTOR = 'button,[role="button"],[role="switch"],[role="slider"],a[href],[tabindex]:not([tabindex="-1"]),[data-active-zone-interactive="true"]';
const EXCLUDE_SELECTOR = '[data-active-zone-exclude="true"]';

function isDisabled(element: HTMLElement): boolean {
  return element.hasAttribute('disabled') || element.getAttribute('aria-disabled') === 'true';
}

function isVisible(element: HTMLElement): boolean {
  const style = window.getComputedStyle(element);
  return style.display !== 'none' && style.visibility !== 'hidden';
}

function getDistanceToCenter(element: HTMLElement, x: number, y: number): number {
  const rect = element.getBoundingClientRect();
  const cx = rect.left + rect.width / 2;
  const cy = rect.top + rect.height / 2;
  return Math.hypot(cx - x, cy - y);
}

function containsPoint(element: HTMLElement, x: number, y: number): boolean {
  const rect = element.getBoundingClientRect();
  return x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom;
}

function getDistanceToRect(element: HTMLElement, x: number, y: number): number {
  const rect = element.getBoundingClientRect();
  const dx = x < rect.left ? rect.left - x : x > rect.right ? x - rect.right : 0;
  const dy = y < rect.top ? rect.top - y : y > rect.bottom ? y - rect.bottom : 0;
  return Math.hypot(dx, dy);
}

export function ActiveZone({ children, className, testId }: Props) {
  const handleClickCapture = useCallback((event: ReactMouseEvent<HTMLDivElement>) => {
    const zone = event.currentTarget;
    const target = event.target as HTMLElement;
    const excludedRoot = target.closest<HTMLElement>(EXCLUDE_SELECTOR);
    if (excludedRoot && zone.contains(excludedRoot)) {
      return;
    }

    const interactiveTarget = target.closest<HTMLElement>(INTERACTIVE_SELECTOR);
    if (interactiveTarget && zone.contains(interactiveTarget)) {
      return;
    }

    const candidates = Array.from(zone.querySelectorAll<HTMLElement>(INTERACTIVE_SELECTOR))
      .filter(el => !el.closest(EXCLUDE_SELECTOR))
      .filter(el => !isDisabled(el) && isVisible(el));
    if (candidates.length === 0) {
      return;
    }

    const preferred = candidates.filter(el => el.dataset.activeZonePrimary === 'true');
    const targetPool = preferred.length > 0 ? preferred : candidates;

    const containingTargets = targetPool.filter(el => containsPoint(el, event.clientX, event.clientY));
    const selectionPool = containingTargets.length > 0 ? containingTargets : targetPool;

    let interactive = selectionPool[0];
    let bestDistance = containingTargets.length > 0
      ? getDistanceToCenter(interactive, event.clientX, event.clientY)
      : getDistanceToRect(interactive, event.clientX, event.clientY);
    for (let i = 1; i < selectionPool.length; i += 1) {
      const distance = containingTargets.length > 0
        ? getDistanceToCenter(selectionPool[i], event.clientX, event.clientY)
        : getDistanceToRect(selectionPool[i], event.clientX, event.clientY);
      if (distance < bestDistance) {
        interactive = selectionPool[i];
        bestDistance = distance;
      }
    }

    if (interactive.dataset.activeZoneAction === 'pointerdown') {
      interactive.dispatchEvent(new MouseEvent('mousedown', {
        bubbles: true,
        cancelable: true,
        clientX: event.clientX,
        clientY: event.clientY,
      }));
      document.dispatchEvent(new MouseEvent('mouseup', {
        bubbles: true,
        cancelable: true,
        clientX: event.clientX,
        clientY: event.clientY,
      }));
      return;
    }

    interactive.click();
    if (typeof interactive.focus === 'function') {
      interactive.focus({ preventScroll: true });
    }
  }, []);

  const zoneClassName = [styles.zone, className].filter(Boolean).join(' ');

  return (
    <div className={zoneClassName} data-active-zone-root="true" data-testid={testId} onClickCapture={handleClickCapture}>
      {children}
    </div>
  );
}
